import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import {
  Container, Typography, Box, Card, CardContent, Grid, Button, TextField,
  Select, MenuItem, FormControl, InputLabel, Alert, Stepper, Step, StepLabel,
  ToggleButton, ToggleButtonGroup, Chip, Divider, CircularProgress,
} from '@mui/material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import TwoWheelerIcon from '@mui/icons-material/TwoWheeler';
import SailingIcon from '@mui/icons-material/Sailing';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import PendingIcon from '@mui/icons-material/Pending';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const steps = ['Type de véhicule', 'Informations', 'Photos', 'Documents', 'Confirmation'];

const vehiclePhotos = [
  { field: 'photoArriere',      label: 'Arrière',       helperText: "Photo de l'arrière complet" },
  { field: 'photoDroit',        label: 'Côté droit',    helperText: 'Photo du côté droit complet' },
  { field: 'photoGauche',       label: 'Côté gauche',   helperText: 'Photo du côté gauche complet' },
  { field: 'photoInterieur',    label: 'Intérieur',     helperText: "Photo de l'intérieur" },
  { field: 'photoTableauBord',  label: 'Tableau bord',  helperText: 'Photo du tableau de bord' },
  { field: 'photoParechoc',     label: 'Pare-chocs',    helperText: 'Photo du pare-chocs avant' },
];

const categoriesByType = {
  auto:   ['Berline', 'SUV', 'Citadine', 'Coupé', 'Cabriolet', 'Utilitaire'],
  moto:   ['Scooter', 'Quad', 'Cyclomoteur', 'Sportive'],
  bateau: ['Barque', 'Bateaux à moteur', 'Jetski', 'Kayak'],
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: 'rgba(85,37,131,0.03)', borderRadius: '12px',
    '& fieldset': { borderColor: 'rgba(85,37,131,0.15)' },
    '&:hover fieldset': { borderColor: 'rgba(85,37,131,0.3)' },
    '&.Mui-focused fieldset': { borderColor: '#552583' },
  },
  '& .MuiInputLabel-root': { color: '#4a4a4a' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#552583' },
  '& input, & textarea': { color: '#1a1a1a' },
};

const RentMyVehicle = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [myVehicles, setMyVehicles] = useState([]);
  const [loadingVehicles, setLoadingVehicles] = useState(true);

  const [form, setForm] = useState({
    vehicleType: 'auto', brand: '', model: '', year: '', category: '',
    transmission: 'Manuelle', fuel: 'Essence', seats: '', luggage: '',
    pricePerDay: '', description: '',
  });

  const [files, setFiles] = useState({
    photoArriere: null, photoDroit: null, photoGauche: null,
    photoInterieur: null, photoTableauBord: null, photoParechoc: null,
    identity: null, carteGrise: null, insuranceDoc: null, controleTechnique: null,
  });

  useEffect(() => { loadMyVehicles(); }, []);

  const loadMyVehicles = async () => {
    try { const r = await api.get('/car-rentals/my-vehicles'); setMyVehicles(r.data.vehicles || []); }
    catch { setMyVehicles([]); } finally { setLoadingVehicles(false); }
  };

  const currentYear = new Date().getFullYear();
  const needsControle = form.year && (currentYear - parseInt(form.year)) >= 4;

  const handleChange = (field, value) => setForm(f => ({ ...f, [field]: value }));
  const handleFile  = (field, file)  => setFiles(f => ({ ...f, [field]: file }));

  const allPhotos = vehiclePhotos.every(p => files[p.field]);
  const canNext = () => {
    if (activeStep === 0) return !!form.vehicleType;
    if (activeStep === 1) return form.brand && form.model && form.year && form.pricePerDay;
    if (activeStep === 2) return allPhotos;
    if (activeStep === 3) return files.identity && files.carteGrise && files.insuranceDoc && (!needsControle || files.controleTechnique);
    return true;
  };

  const handleSubmit = async () => {
    setLoading(true); setError('');
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v) fd.append(k, v); });
      Object.entries(files).forEach(([k, f]) => { if (f) fd.append(k, f); });
      await api.post('/car-rentals/my-vehicles', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSuccess(true); loadMyVehicles();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la soumission');
    } finally { setLoading(false); }
  };

  const resetForm = () => {
    setSuccess(false); setActiveStep(0);
    setForm({ vehicleType: 'auto', brand: '', model: '', year: '', category: '', transmission: 'Manuelle', fuel: 'Essence', seats: '', luggage: '', pricePerDay: '', description: '' });
    setFiles({ photoArriere: null, photoDroit: null, photoGauche: null, photoInterieur: null, photoTableauBord: null, photoParechoc: null, identity: null, carteGrise: null, insuranceDoc: null, controleTechnique: null });
  };

  const getStatusChip = (status) => {
    const styles = {
      verified: { bgcolor: 'rgba(85,37,131,0.1)', color: '#552583', icon: <CheckCircleIcon sx={{ fontSize: 14 }} /> },
      rejected: { bgcolor: 'rgba(231,76,60,0.1)', color: '#e74c3c', icon: <WarningIcon sx={{ fontSize: 14 }} /> },
    };
    const s = styles[status] || { bgcolor: 'rgba(253,185,39,0.15)', color: '#c98a00', icon: <PendingIcon sx={{ fontSize: 14 }} /> };
    const label = status === 'verified' ? 'Approuvé' : status === 'rejected' ? 'Refusé' : 'En vérification';
    return <Chip icon={s.icon} label={label} size="small" sx={{ bgcolor: s.bgcolor, color: s.color, fontWeight: 700 }} />;
  };

  const FileBox = ({ label, field, required, helperText }) => (
    <Button component="label" fullWidth sx={{
      p: 2.5, borderRadius: '14px', display: 'flex', flexDirection: 'column',
      border: files[field] ? '2px solid #552583' : '2px dashed rgba(85,37,131,0.25)',
      bgcolor: files[field] ? 'rgba(85,37,131,0.05)' : 'rgba(85,37,131,0.02)',
      textAlign: 'center', textTransform: 'none', color: '#1a1a1a',
      transition: 'all 0.25s',
      '&:hover': { borderColor: '#552583', bgcolor: 'rgba(85,37,131,0.06)' },
    }}>
      <input type="file" accept="image/*,.pdf" hidden onChange={e => { handleFile(field, e.target.files[0]); e.target.value = ''; }} />
      {files[field] ? (
        <>
          <CheckCircleIcon sx={{ fontSize: 36, color: '#552583', mb: 1 }} />
          <Typography variant="body2" fontWeight={600} sx={{ color: '#552583' }}>{files[field].name}</Typography>
        </>
      ) : (
        <>
          <CloudUploadIcon sx={{ fontSize: 36, color: 'rgba(85,37,131,0.35)', mb: 1 }} />
          <Typography variant="body2" fontWeight={600} sx={{ color: '#1a1a1a' }}>{label}{required && ' *'}</Typography>
          {helperText && <Typography variant="caption" sx={{ color: '#7a7a7a', display: 'block', mt: 0.5 }}>{helperText}</Typography>}
        </>
      )}
    </Button>
  );

  return (
    <Box sx={{ background: '#E4F0F6', minHeight: '100vh' }}>

      {/* En-tête */}
      <Box sx={{ background: 'linear-gradient(135deg, #552583 0%, #3a1760 100%)', py: 6, position: 'relative', overflow: 'hidden' }}>
        <Box sx={{ position: 'absolute', top: '-60px', right: '-60px', width: 300, height: 300, borderRadius: '50%', background: 'radial-gradient(circle, rgba(253,185,39,0.2) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/car-rental')}
            sx={{ color: 'rgba(255,255,255,0.65)', mb: 3, textTransform: 'none', fontWeight: 600, '&:hover': { color: '#FDB927' } }}>
            Retour
          </Button>
          <Typography variant="h3" fontWeight={900} sx={{ color: 'white', textTransform: 'none', letterSpacing: 0, mb: 1 }}>
            Mettre mon véhicule{' '}
            <Box component="span" sx={{ background: 'linear-gradient(135deg, #FDB927, #ffd060)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              en location
            </Box>
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.65)', maxWidth: 520 }}>
            Louez votre véhicule à Mayotte et générez des revenus. Fournissez vos documents, nous vérifions et vous êtes en ligne.
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: 5 }}>

        {/* Mes véhicules existants */}
        {!loadingVehicles && myVehicles.length > 0 && (
          <Box sx={{ mb: 5 }}>
            <Typography variant="h6" fontWeight={700} sx={{ color: '#1a1a1a', mb: 2 }}>Mes véhicules</Typography>
            <Grid container spacing={2}>
              {myVehicles.map(v => (
                <Grid item xs={12} sm={6} key={v.id}>
                  <Card sx={{ background: 'white', border: '1px solid rgba(85,37,131,0.1)', boxShadow: '0 2px 12px rgba(85,37,131,0.06)', '&:hover': { transform: 'none' } }}>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#1a1a1a' }}>
                          {v.brand} {v.model} ({v.year})
                        </Typography>
                        {getStatusChip(v.verificationStatus)}
                      </Box>
                      <Typography variant="body2" sx={{ color: '#4a4a4a', fontWeight: 600 }}>{v.pricePerDay}€/jour</Typography>
                      {v.verificationStatus === 'rejected' && v.rejectionReason && (
                        <Alert severity="error" sx={{ mt: 1, borderRadius: 2 }}>{v.rejectionReason}</Alert>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
            <Divider sx={{ my: 4, borderColor: 'rgba(85,37,131,0.1)' }} />
          </Box>
        )}

        {/* Succès */}
        {success ? (
          <Card sx={{ background: 'white', border: '2px solid rgba(85,37,131,0.2)', boxShadow: '0 8px 40px rgba(85,37,131,0.12)', borderRadius: '20px !important', '&:hover': { transform: 'none' } }}>
            <CardContent sx={{ textAlign: 'center', py: 7 }}>
              <Box sx={{ width: 90, height: 90, borderRadius: '50%', background: 'linear-gradient(135deg, #552583, #FDB927)', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3 }}>
                <CheckCircleIcon sx={{ fontSize: 48, color: 'white' }} />
              </Box>
              <Typography variant="h5" fontWeight={800} sx={{ color: '#1a1a1a', mb: 1 }}>Véhicule soumis avec succès !</Typography>
              <Typography variant="body1" sx={{ color: '#4a4a4a', mb: 4, maxWidth: 400, mx: 'auto' }}>
                Vos documents sont en cours de vérification. Vous serez notifié sous 24–48h une fois votre véhicule approuvé.
              </Typography>
              <Box display="flex" gap={2} justifyContent="center" flexWrap="wrap">
                <Button variant="contained" onClick={resetForm}
                  sx={{ background: 'linear-gradient(135deg, #552583, #FDB927)', color: 'white', fontWeight: 700, borderRadius: '12px', textTransform: 'none', px: 4 }}>
                  Ajouter un autre véhicule
                </Button>
                <Button variant="outlined" onClick={() => navigate('/car-rental')}
                  sx={{ borderColor: '#552583', color: '#552583', borderWidth: 2, fontWeight: 700, borderRadius: '12px', textTransform: 'none', px: 4, '&:hover': { borderWidth: 2 } }}>
                  Retour aux locations
                </Button>
              </Box>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Stepper */}
            <Stepper activeStep={activeStep} alternativeLabel sx={{
              mb: 4,
              '& .MuiStepLabel-label': { color: '#7a7a7a', fontWeight: 600, fontSize: '0.8rem' },
              '& .MuiStepLabel-label.Mui-active': { color: '#552583', fontWeight: 700 },
              '& .MuiStepLabel-label.Mui-completed': { color: '#552583' },
              '& .MuiStepIcon-root': { color: 'rgba(85,37,131,0.15)' },
              '& .MuiStepIcon-root.Mui-active': { color: '#552583' },
              '& .MuiStepIcon-root.Mui-completed': { color: '#FDB927' },
              '& .MuiStepConnector-line': { borderColor: 'rgba(85,37,131,0.15)' },
            }}>
              {steps.map(l => <Step key={l}><StepLabel>{l}</StepLabel></Step>)}
            </Stepper>

            {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}

            <Card sx={{ background: 'white', border: '1px solid rgba(85,37,131,0.1)', boxShadow: '0 4px 24px rgba(85,37,131,0.08)', borderRadius: '20px !important', '&:hover': { transform: 'none' } }}>
              <CardContent sx={{ p: { xs: 3, sm: 4 } }}>

                {/* Étape 0 : Type */}
                {activeStep === 0 && (
                  <Box>
                    <Typography variant="h6" fontWeight={700} sx={{ color: '#1a1a1a', mb: 3 }}>
                      Quel type de véhicule souhaitez-vous louer ?
                    </Typography>
                    <ToggleButtonGroup value={form.vehicleType} exclusive onChange={(e, v) => { if (v) handleChange('vehicleType', v); }}
                      sx={{ display: 'flex', gap: 2, flexWrap: 'wrap',
                        '& .MuiToggleButton-root': {
                          flex: 1, minWidth: 130, py: 4, flexDirection: 'column', gap: 1,
                          color: '#4a4a4a', border: '2px solid rgba(85,37,131,0.15) !important',
                          borderRadius: '16px !important', textTransform: 'none', background: 'rgba(85,37,131,0.02)',
                          transition: 'all 0.25s',
                          '&.Mui-selected': { bgcolor: 'rgba(85,37,131,0.08)', color: '#552583', border: '2px solid #552583 !important', boxShadow: '0 4px 20px rgba(85,37,131,0.2)' },
                          '&:hover': { bgcolor: 'rgba(85,37,131,0.05)' },
                        },
                      }}>
                      {[{ v: 'auto', icon: <DirectionsCarIcon sx={{ fontSize: 44 }} />, l: 'Voiture' },
                        { v: 'moto', icon: <TwoWheelerIcon sx={{ fontSize: 44 }} />, l: 'Moto' },
                        { v: 'bateau', icon: <SailingIcon sx={{ fontSize: 44 }} />, l: 'Bateau' }].map(t => (
                        <ToggleButton key={t.v} value={t.v}>{t.icon}<Typography fontWeight={700}>{t.l}</Typography></ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                  </Box>
                )}

                {/* Étape 1 : Infos */}
                {activeStep === 1 && (
                  <Box>
                    <Typography variant="h6" fontWeight={700} sx={{ color: '#1a1a1a', mb: 3 }}>Informations du véhicule</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}><TextField fullWidth label="Marque *" value={form.brand} onChange={e => handleChange('brand', e.target.value)} sx={fieldSx} /></Grid>
                      <Grid item xs={12} sm={6}><TextField fullWidth label="Modèle *" value={form.model} onChange={e => handleChange('model', e.target.value)} sx={fieldSx} /></Grid>
                      <Grid item xs={12} sm={4}><TextField fullWidth label="Année *" type="number" value={form.year} onChange={e => handleChange('year', e.target.value)} inputProps={{ min: 1990, max: currentYear }} sx={fieldSx} /></Grid>
                      <Grid item xs={12} sm={4}>
                        <FormControl fullWidth sx={fieldSx}>
                          <InputLabel>Catégorie</InputLabel>
                          <Select value={form.category} onChange={e => handleChange('category', e.target.value)} label="Catégorie">
                            {(categoriesByType[form.vehicleType] || []).map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <FormControl fullWidth sx={fieldSx}>
                          <InputLabel>Carburant</InputLabel>
                          <Select value={form.fuel} onChange={e => handleChange('fuel', e.target.value)} label="Carburant">
                            {['Essence', 'Diesel', 'Électrique'].map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}
                          </Select>
                        </FormControl>
                      </Grid>
                      {form.vehicleType === 'auto' && (<>
                        <Grid item xs={6} sm={4}>
                          <FormControl fullWidth sx={fieldSx}>
                            <InputLabel>Transmission</InputLabel>
                            <Select value={form.transmission} onChange={e => handleChange('transmission', e.target.value)} label="Transmission">
                              <MenuItem value="Automatique">Automatique</MenuItem>
                              <MenuItem value="Manuelle">Manuelle</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={6} sm={4}><TextField fullWidth label="Places" type="number" value={form.seats} onChange={e => handleChange('seats', e.target.value)} sx={fieldSx} /></Grid>
                        <Grid item xs={6} sm={4}><TextField fullWidth label="Bagages" type="number" value={form.luggage} onChange={e => handleChange('luggage', e.target.value)} sx={fieldSx} /></Grid>
                      </>)}
                      {form.vehicleType === 'moto' && (
                        <Grid item xs={6} sm={4}><TextField fullWidth label="Places" type="number" value={form.seats} onChange={e => handleChange('seats', e.target.value)} inputProps={{ min: 1, max: 2 }} sx={fieldSx} /></Grid>
                      )}
                      {form.vehicleType === 'bateau' && (
                        <Grid item xs={6} sm={4}><TextField fullWidth label="Passagers max" type="number" value={form.seats} onChange={e => handleChange('seats', e.target.value)} sx={fieldSx} /></Grid>
                      )}
                      <Grid item xs={12} sm={6}><TextField fullWidth label="Prix par jour (€) *" type="number" value={form.pricePerDay} onChange={e => handleChange('pricePerDay', e.target.value)} sx={fieldSx} /></Grid>
                      <Grid item xs={12}><TextField fullWidth multiline rows={3} label="Description" value={form.description} onChange={e => handleChange('description', e.target.value)} sx={fieldSx} /></Grid>
                    </Grid>
                    {form.year && needsControle && (
                      <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                        Votre véhicule a plus de 4 ans — le contrôle technique sera obligatoire à l'étape suivante.
                      </Alert>
                    )}
                  </Box>
                )}

                {/* Étape 2 : Photos */}
                {activeStep === 2 && (
                  <Box>
                    <Typography variant="h6" fontWeight={700} sx={{ color: '#1a1a1a', mb: 0.5 }}>Photos du véhicule</Typography>
                    <Typography variant="body2" sx={{ color: '#4a4a4a', mb: 1 }}>6 photos obligatoires pour l'état des lieux</Typography>

                    <Box sx={{ mb: 3, p: 2, borderRadius: 2, bgcolor: 'rgba(85,37,131,0.05)', border: '1px solid rgba(85,37,131,0.15)', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <PhotoCameraIcon sx={{ color: '#552583' }} />
                      <Box>
                        <Typography variant="body2" fontWeight={700} sx={{ color: '#552583' }}>
                          {vehiclePhotos.filter(p => files[p.field]).length} / {vehiclePhotos.length} photos ajoutées
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#7a7a7a' }}>JPG, PNG, WEBP — max 10 Mo par photo</Typography>
                      </Box>
                    </Box>

                    <Grid container spacing={2}>
                      {vehiclePhotos.map(photo => (
                        <Grid item xs={12} sm={6} key={photo.field}>
                          <Button component="label" fullWidth sx={{
                            p: 0, borderRadius: '12px', display: 'flex', flexDirection: 'column', overflow: 'hidden',
                            border: files[photo.field] ? '2px solid #552583' : '2px dashed rgba(85,37,131,0.2)',
                            bgcolor: files[photo.field] ? 'rgba(85,37,131,0.03)' : 'rgba(85,37,131,0.02)',
                            textTransform: 'none', transition: 'all 0.25s', minHeight: 0,
                            '&:hover': { borderColor: '#552583', bgcolor: 'rgba(85,37,131,0.05)' },
                          }}>
                            <input type="file" accept="image/*" hidden onChange={e => { handleFile(photo.field, e.target.files[0]); e.target.value = ''; }} />
                            {files[photo.field] ? (
                              <Box sx={{ width: '100%' }}>
                                <Box sx={{ height: 130, overflow: 'hidden' }}>
                                  <img src={URL.createObjectURL(files[photo.field])} alt={photo.label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                </Box>
                                <Box sx={{ p: 1.2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <CheckCircleIcon sx={{ fontSize: 16, color: '#552583' }} />
                                  <Typography variant="caption" fontWeight={700} sx={{ color: '#552583' }}>{photo.label}</Typography>
                                </Box>
                              </Box>
                            ) : (
                              <Box sx={{ p: 3, textAlign: 'center', width: '100%' }}>
                                <PhotoCameraIcon sx={{ fontSize: 32, color: 'rgba(85,37,131,0.3)', mb: 1 }} />
                                <Typography variant="body2" fontWeight={600} sx={{ color: '#1a1a1a' }}>{photo.label} *</Typography>
                                <Typography variant="caption" sx={{ color: '#7a7a7a', display: 'block', mt: 0.5 }}>{photo.helperText}</Typography>
                              </Box>
                            )}
                          </Button>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}

                {/* Étape 3 : Documents */}
                {activeStep === 3 && (
                  <Box>
                    <Typography variant="h6" fontWeight={700} sx={{ color: '#1a1a1a', mb: 0.5 }}>Documents requis</Typography>
                    <Typography variant="body2" sx={{ color: '#4a4a4a', mb: 3 }}>JPG, PNG, PDF — max 10 Mo par fichier</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}><FileBox label="Pièce d'identité" field="identity" required helperText="CNI, passeport ou permis de séjour" /></Grid>
                      <Grid item xs={12} sm={6}><FileBox label="Carte grise" field="carteGrise" required helperText="Certificat d'immatriculation" /></Grid>
                      <Grid item xs={12} sm={6}><FileBox label="Attestation d'assurance" field="insuranceDoc" required helperText="Assurance en cours de validité" /></Grid>
                      {needsControle && (
                        <Grid item xs={12} sm={6}><FileBox label="Contrôle technique" field="controleTechnique" required helperText="Obligatoire pour les véhicules +4 ans" /></Grid>
                      )}
                    </Grid>
                  </Box>
                )}

                {/* Étape 4 : Récapitulatif */}
                {activeStep === 4 && (
                  <Box>
                    <Typography variant="h6" fontWeight={700} sx={{ color: '#1a1a1a', mb: 3 }}>Récapitulatif</Typography>
                    <Box sx={{ bgcolor: 'rgba(85,37,131,0.04)', p: 3, borderRadius: '14px', border: '1px solid rgba(85,37,131,0.1)' }}>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="caption" sx={{ color: '#7a7a7a' }}>Type</Typography>
                          <Typography fontWeight={700} sx={{ color: '#1a1a1a' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {form.vehicleType === 'auto' ? <DirectionsCarIcon sx={{ fontSize: 18 }} /> : form.vehicleType === 'moto' ? <TwoWheelerIcon sx={{ fontSize: 18 }} /> : <SailingIcon sx={{ fontSize: 18 }} />}
                              {form.vehicleType === 'auto' ? 'Voiture' : form.vehicleType === 'moto' ? 'Moto' : 'Bateau'}
                            </Box>
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" sx={{ color: '#7a7a7a' }}>Véhicule</Typography>
                          <Typography fontWeight={700} sx={{ color: '#1a1a1a' }}>{form.brand} {form.model} ({form.year})</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" sx={{ color: '#7a7a7a' }}>Prix/jour</Typography>
                          <Typography fontWeight={800} sx={{ color: '#552583', fontSize: '1.1rem' }}>{form.pricePerDay}€</Typography>
                        </Grid>
                        <Grid item xs={12}><Divider sx={{ borderColor: 'rgba(85,37,131,0.1)' }} /></Grid>
                        <Grid item xs={12}>
                          <Typography variant="caption" sx={{ color: '#7a7a7a' }}>Photos fournies</Typography>
                          <Box display="flex" gap={1} mt={0.5} flexWrap="wrap">
                            {vehiclePhotos.map(p => (
                              <Chip key={p.field} icon={<PhotoCameraIcon sx={{ fontSize: 14 }} />} label={p.label} size="small"
                                sx={{ bgcolor: 'rgba(85,37,131,0.08)', color: '#552583', fontWeight: 600 }} />
                            ))}
                          </Box>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="caption" sx={{ color: '#7a7a7a' }}>Documents fournis</Typography>
                          <Box display="flex" gap={1} mt={0.5} flexWrap="wrap">
                            {["Pièce d'identité", 'Carte grise', 'Assurance', ...(needsControle ? ['Contrôle technique'] : [])].map(d => (
                              <Chip key={d} icon={<DescriptionIcon sx={{ fontSize: 14 }} />} label={d} size="small"
                                sx={{ bgcolor: 'rgba(253,185,39,0.12)', color: '#c98a00', fontWeight: 600 }} />
                            ))}
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                    <Alert severity="info" sx={{ mt: 3, borderRadius: 2, bgcolor: 'rgba(85,37,131,0.05)', border: '1px solid rgba(85,37,131,0.15)', color: '#552583', '& .MuiAlert-icon': { color: '#552583' } }}>
                      Après soumission, nos équipes vérifieront vos documents sous 24 à 48h. Votre véhicule sera mis en ligne une fois approuvé.
                    </Alert>
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Navigation */}
            <Box display="flex" justifyContent="space-between" mt={3}>
              <Button onClick={() => setActiveStep(s => s - 1)} disabled={activeStep === 0} startIcon={<ArrowBackIcon />}
                sx={{ color: '#552583', fontWeight: 700, textTransform: 'none', borderRadius: '12px', px: 3, '&:disabled': { color: 'rgba(0,0,0,0.25)' } }}>
                Retour
              </Button>
              {activeStep < steps.length - 1 ? (
                <Button variant="contained" onClick={() => setActiveStep(s => s + 1)} disabled={!canNext()} endIcon={<ArrowForwardIcon />}
                  sx={{ background: 'linear-gradient(135deg, #552583, #FDB927)', color: 'white', fontWeight: 700, px: 4, borderRadius: '12px', textTransform: 'none', boxShadow: '0 4px 16px rgba(85,37,131,0.3)', '&:disabled': { background: 'rgba(85,37,131,0.1)', color: 'rgba(0,0,0,0.3)' } }}>
                  Suivant
                </Button>
              ) : (
                <Button variant="contained" onClick={handleSubmit} disabled={loading}
                  startIcon={loading ? <CircularProgress size={18} sx={{ color: 'white' }} /> : null}
                  sx={{ background: 'linear-gradient(135deg, #552583, #FDB927)', color: 'white', fontWeight: 700, px: 5, borderRadius: '12px', textTransform: 'none', boxShadow: '0 8px 30px rgba(85,37,131,0.3)' }}>
                  {loading ? 'Soumission…' : 'Soumettre mon véhicule'}
                </Button>
              )}
            </Box>
          </>
        )}
      </Container>
    </Box>
  );
};

export default RentMyVehicle;
